import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import Layout from './components/Layout';
import BrandGhostie from './components/BrandGhostie';
import { ART } from './lib/art';
import { GA_MEASUREMENT_ID } from './lib/analytics';

import Home from './pages/Home';
import Book from './pages/Book';
import BookingSuccess from './pages/BookingSuccess';
import BecomeGhostie from './pages/BecomeGhostie';
import About from './pages/About';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import GiftCards from './pages/GiftCards';
import Help from './pages/Help';
import Reviews from './pages/Reviews';
import Contacts from './pages/Contacts';
import FAQ from './pages/FAQ';
import Bio from './pages/Bio';
import Cities from './pages/Cities';
import CityPage from './pages/CityPage';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import HostDashboard from './pages/HostDashboard';
import AdminDashboard from './pages/AdminDashboard';
import TeamContent from './pages/TeamContent';
import Legal from './pages/Legal';

const PREVIEW_PARAM = 'ghostie_preview';
const PRELAUNCH_MODE = process.env.REACT_APP_PRELAUNCH_MODE === 'true';

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#a3a3a3]">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role && !(role === 'host' && user.role === 'admin')) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function ComingSoon() {
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#050505] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(249,115,22,0.18),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(63,46,30,0.34),transparent_32%)]" />
      <img
        src={ART.hero}
        alt=""
        className="pointer-events-none absolute -left-10 bottom-8 hidden md:block w-44 opacity-70 rotate-[-8deg]"
        loading="eager"
      />
      <img
        src={ART.tarot}
        alt=""
        className="pointer-events-none absolute -right-8 top-20 hidden md:block w-36 opacity-60 rotate-[10deg]"
        loading="eager"
      />
      <main className="relative z-10 max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#3f2e1e] bg-[#171717]/80 px-4 py-2 text-xs uppercase tracking-[0.28em] text-[#f97316] font-accent font-bold mb-6">
          Something strange is waking up
        </div>
        <h1 className="text-6xl sm:text-8xl font-heading font-semibold leading-none mb-6">
          <BrandGhostie /> is coming soon.
        </h1>
        <p className="text-lg sm:text-xl text-[#c7c7c7] leading-relaxed max-w-2xl mx-auto">
          We are polishing the candles, feeding the mascot, and preparing mysterious local experiences for curious travellers.
        </p>
      </main>
    </div>
  );
}

function PrivateRoutes() {
  const location = useLocation();
  const routes = (
    <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book" element={<Book />} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/start" element={<BecomeGhostie />} />
        <Route path="/about" element={<About />} />
        <Route path="/cities" element={<Cities />} />
        <Route path="/cities/:slug" element={<CityPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/help" element={<Help />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/souvenirs" element={<Navigate to="/" replace />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contacts" element={<Contacts />} />
        <Route path="/bio" element={<Bio />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Login mode="signup" />} />
        <Route path="/team" element={<Login />} />
        <Route path="/legal/:slug" element={<Legal />} />
        {/* legacy redirects */}
        <Route path="/whats-ghostie" element={<Navigate to="/about" replace />} />
        <Route path="/what-is-ghostie" element={<Navigate to="/about" replace />} />
        <Route path="/contact-us" element={<Navigate to="/contacts" replace />} />
        <Route path="/become-a-ghostie" element={<Navigate to="/start" replace />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/host" element={<ProtectedRoute role="host"><HostDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/team/content" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/blog" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/blog/:post_id" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/pins" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/pins/:pin_id" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/calendar" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/cities" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/media" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/settings" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="/team/content/generation-runs" element={<ProtectedRoute role="admin"><TeamContent /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );

  if (location.pathname === '/bio') return routes;
  return (
    <Layout>
      {routes}
    </Layout>
  );
}

function AppRouter() {
  const location = useLocation();
  const [previewEnabled, setPreviewEnabled] = useState(() => sessionStorage.getItem('ghostie_preview_enabled') === 'true');

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || document.querySelector(`script[src*="${GA_MEASUREMENT_ID}"]`)) return;
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = window.gtag || function gtag(){ window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get(PREVIEW_PARAM) === '1') {
      sessionStorage.setItem('ghostie_preview_enabled', 'true');
      setPreviewEnabled(true);
    }
  }, [location.search]);

  useEffect(() => {
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', PRELAUNCH_MODE ? 'noindex,nofollow' : 'index,follow');
  }, []);

  if (PRELAUNCH_MODE && !previewEnabled) {
    return <ComingSoon />;
  }

  return <PrivateRoutes />;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
